import React, { useState, useRef, useCallback, useEffect } from 'react';
import { select } from 'd3-selection';
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import type { ZoomBehavior } from 'd3-zoom';
import 'd3-transition';
import { MapArtwork, mapPorts } from './MapArtworkGenerated';
import { RouteInfoPanel } from './RouteInfoPanel';
import routesData from '../data/routes.json';
import stopsData from '../data/stops.json';
import type { StopsData, Route, Stop } from '../types/ferry';
import { useLanguage } from '../contexts/LanguageContext';

const VIEWBOX_WIDTH = 827;
const VIEWBOX_HEIGHT = 541;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;

interface MapPlaceholderProps {
  className?: string;
  setFromStop?: (stopId: string) => void;
  setToStop?: (stopId: string) => void;
  onDeparturePortClick?: (portId: string) => void;
  externalSelectedPort?: string | null;
  hoveredRouteId?: string | null;
}


export const MapPlaceholder: React.FC<MapPlaceholderProps> = ({ className = "", setFromStop, setToStop, onDeparturePortClick, externalSelectedPort, hoveredRouteId }) => {
  const { language } = useLanguage();
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);

  // Style Configuration Templates
  const styleConfig = {
    ports: {
      default: {
        fill: '#FEC638',
        stroke: 'black',
        strokeWidth: '1'
      },
      hover: {
        fill: '#3B82F6',
        strokeWidth: '2'
      }
    },
    routes: {
      default: {
        stroke: '#b8b8b8',
        strokeWidth: '2',
        strokeDasharray: 'none',
        opacity: '0.8'
      },
      hover: {
        stroke: '#ef4444',
        strokeWidth: '3',
        opacity: '1'
      }
    },
    portLabels: {
      chinese: {
        fontSize: '13',
        fontWeight: '700',
        fill: '#1F2937',
        fontFamily: 'Noto Serif SC, Noto Serif, serif'
      },
      japanese: {
        fontSize: '13',
        fontWeight: '700',
        fill: '#1F2937',
        fontFamily: 'Noto Serif JP, Noto Serif, serif'
      },
      english: {
        fontSize: '9',
        fontWeight: '700',
        fill: '#1F2937',
        fontFamily: 'Noto Serif, sans-serif'
      },
      spacing: {
        verticalOffset: 20,
        primaryOffset: -3,
        secondaryOffset: 7
      }
    },
    regionLabels: {
      prefecture: {
        fontSize: '18',
        fontWeight: '600',
        fill: '#555555',
        opacity: '0.8'
      },
      island: {
        fontSize: '14',
        fontWeight: '600',
        fill: '#555555',
        opacity: '0.8'
      }
    }
  };

  // Template Functions

  const getChineseLabelStyles = () => ({
    fontSize: styleConfig.portLabels.chinese.fontSize,
    fontWeight: styleConfig.portLabels.chinese.fontWeight,
    fill: styleConfig.portLabels.chinese.fill,
    fontFamily: styleConfig.portLabels.chinese.fontFamily,
    textAnchor: "middle" as const
  });

  const getJapaneseLabelStyles = () => ({
    fontSize: styleConfig.portLabels.japanese.fontSize,
    fontWeight: styleConfig.portLabels.japanese.fontWeight,
    fill: styleConfig.portLabels.japanese.fill,
    fontFamily: styleConfig.portLabels.japanese.fontFamily,
    textAnchor: "middle" as const
  });

  const getEnglishLabelStyles = () => ({
    fontSize: styleConfig.portLabels.english.fontSize,
    fontWeight: styleConfig.portLabels.english.fontWeight,
    fill: styleConfig.portLabels.english.fill,
    fontFamily: styleConfig.portLabels.english.fontFamily,
    textAnchor: "middle" as const
  });

  // State
  const [zoomLevel, setZoomLevel] = useState(1);
  const [currentTransform, setCurrentTransform] = useState<{ k: number; x: number; y: number }>({ k: 1, x: 0, y: 0 });
  const zoomBehavior = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Panel state - store SVG coordinates
  const [selectedPort, setSelectedPort] = useState<{ id: string; name: { en: string; zh: string }; svgPosition: { x: number; y: number } } | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<{ route: Route; fromStop: Stop; toStop: Stop; svgPosition: { x: number; y: number } } | null>(null);

  // Helper function to convert SVG coordinates to screen coordinates
  const svgToScreenCoords = useCallback((svgX: number, svgY: number) => {
    if (!svgRef.current) return { x: 0, y: 0 };

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();

    // Calculate scale factors between SVG viewBox and rendered size
    const scaleX = rect.width / VIEWBOX_WIDTH;
    const scaleY = rect.height / VIEWBOX_HEIGHT;

    // Apply the scale factors and D3 transform
    const screenX = svgX * scaleX * currentTransform.k + currentTransform.x + rect.left;
    const screenY = svgY * scaleY * currentTransform.k + currentTransform.y + rect.top;

    return { x: screenX, y: screenY };
  }, [currentTransform]);

  // Helper function to map port IDs to stop IDs
  const mapPortIdToStopId = useCallback((portId: string): string => {
    const stops = stopsData as StopsData;

    // Handle specific ID mismatches between map-config.json and stops.json
    const idMapping: Record<string, string> = {
      'shinokayama': 'shin_okayama',
      'takamatsu_higashi': 'takamatsu_higashi'
    };

    // Check for direct mapping first
    if (idMapping[portId]) {
      return idMapping[portId];
    }

    // Try direct match
    if (stops[portId as keyof typeof stops]) {
      return portId;
    }

    // Try with common prefixes for island stops
    const prefixes = ['naoshima_', 'teshima_', 'shodoshima_'];
    for (const prefix of prefixes) {
      const prefixedId = prefix + portId;
      if (stops[prefixedId as keyof typeof stops]) {
        return prefixedId;
      }
    }

    // Return original if no match found
    return portId;
  }, []);

  // Port and Route click handlers
  const handlePortClick = useCallback((portId: string) => {
    // Find port data from mapPorts (case-insensitive)
    const cleanId = portId.replace('_port', '').toLowerCase();
    const port = mapPorts.find(p => p.id.toLowerCase() === cleanId);
    if (!port) return;

    // Map port ID to stop ID
    const stopId = mapPortIdToStopId(port.id);

    // Calculate center position for panel
    const centerX = port.x + port.width / 2;
    const centerY = port.y + port.height / 2;

    // Toggle: if clicking the same port, deselect it
    if (selectedPort && selectedPort.id === stopId) {
      setSelectedPort(null);
      return;
    }

    // Always set the selected port for highlighting effect
    setSelectedPort({
      id: stopId, // Use the mapped stop ID
      name: { en: port.en, zh: port.zh },
      svgPosition: { x: centerX, y: centerY }
    });
    setSelectedRoute(null); // Close route panel if open

    // If departure port click handler is provided, also trigger it
    if (onDeparturePortClick) {
      onDeparturePortClick(stopId);
    }
  }, [mapPortIdToStopId, selectedPort, onDeparturePortClick]);

  const handleRouteClick = useCallback((routeId: string) => {
    // Strip "route_" prefix from SVG route IDs
    const cleanId = routeId.replace('route_', '');

    // Map SVG route IDs to routes.json IDs (handle naming inconsistencies)
    const routeIdMapping: Record<string, string> = {
      'takamatsu_miyanoura': 'takamatsu_naoshima',
      'uno_miyanoura': 'uno_naoshima',
      'uno_honmura': 'uno_naoshima_honmura',
      'miyanoura_ieura_inujima': 'naoshima_teshima_inujima',
      'tonosho_miyanoura': 'shodoshima_tonosho_naoshima_miyanoura',
      'takamatsu_karato': 'takamatsu_teshima_karato',
      'takamatsu_ikeda': 'takamatsu_shodoshima_ikeda',
      'shinokayama_tonosho': 'shin_okayama_tonosho',
      'tonosho_inujima': 'shodoshima_tonosho_inujima',
    };

    const mappedId = routeIdMapping[cleanId] || cleanId;

    // Find the route data
    const route = routesData.find((r: Route) => r.id === mappedId);
    if (!route || route.stops.length < 2) return;

    // Get first and last stops as default From/To
    const fromStopId = route.stops[0];
    const toStopId = route.stops[route.stops.length - 1];

    const stops = stopsData as StopsData;
    const fromStop = stops[fromStopId as keyof typeof stops];
    const toStop = stops[toStopId as keyof typeof stops];

    if (!fromStop || !toStop) return;

    // For now, use center of viewport as route panel position
    // TODO: Calculate actual route center from SVG path
    const centerX = VIEWBOX_WIDTH / 2;
    const centerY = VIEWBOX_HEIGHT / 2;

    setSelectedRoute({
      route,
      fromStop,
      toStop,
      svgPosition: { x: centerX, y: centerY }
    });
    setSelectedPort(null); // Close port panel if open
  }, []);

  // D3 Button handlers
  const handleZoomIn = useCallback(() => {
    if (zoomBehavior.current && svgRef.current) {
      const svg = select(svgRef.current);
      svg.transition().duration(200).call(
        zoomBehavior.current.scaleBy, 1.5
      );
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (zoomBehavior.current && svgRef.current) {
      const svg = select(svgRef.current);
      svg.transition().duration(200).call(
        zoomBehavior.current.scaleBy, 1 / 1.5
      );
    }
  }, []);

  const resetView = useCallback(() => {
    if (zoomBehavior.current && svgRef.current) {
      const svg = select(svgRef.current);
      svg.transition().duration(500).call(
        zoomBehavior.current.transform,
        zoomIdentity
      );
    }
  }, []);

  // Sync external selected port with internal state
  useEffect(() => {
    if (externalSelectedPort === null) {
      setSelectedPort(null);
      return;
    }

    if (externalSelectedPort && externalSelectedPort !== selectedPort?.id) {
      // Find the port data for highlighting
      const port = mapPorts.find(p => {
        const stopId = mapPortIdToStopId(p.id);
        return stopId === externalSelectedPort;
      });

      if (port) {
        const centerX = port.x + port.width / 2;
        const centerY = port.y + port.height / 2;

        setSelectedPort({
          id: externalSelectedPort,
          name: { en: port.en, zh: port.zh },
          svgPosition: { x: centerX, y: centerY }
        });
      }
    }
  }, [externalSelectedPort, selectedPort?.id, mapPortIdToStopId]);

  // Route and port highlighting effect
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = select(svgRef.current);

    // Remove all existing highlights
    svg.selectAll('[id^="route"]').classed('highlighted', false).classed('hovered', false);
    svg.selectAll('[id$="_port"]').classed('selected', false).classed('connected', false).classed('inactive', false);

    if (selectedPort) {
      // Find all routes connected to this port
      const connectedRoutes = routesData.filter((route: Route) => {
        return route.stops.some(stop =>
          stop === selectedPort.id
        );
      });

      // Find all connected stop IDs (not port IDs)
      const connectedStopIds = new Set<string>();
      connectedRoutes.forEach((route: Route) => {
        route.stops.forEach(stopId => {
          if (stopId !== selectedPort.id) {
            connectedStopIds.add(stopId);
          }
        });
      });

      // Apply classes to ports - compare at stop ID level
      svg.selectAll('[id$="_port"]').each(function() {
        const element = select(this);
        const portId = element.attr('id');

        // Get the stop ID for this port
        const portBaseId = portId?.replace('_port', '').toLowerCase();
        const portStopId = mapPortIdToStopId(portBaseId || '');

        if (portStopId === selectedPort.id) {
          // This is the selected port - make it blue
          element.classed('selected', true);
        } else if (connectedStopIds.has(portStopId)) {
          // This is a connected port - keep yellow (default)
          element.classed('connected', true);
        } else {
          // This is an unconnected port - make it grey
          element.classed('inactive', true);
        }
      });

      // Highlight connected routes (add "route_" prefix for SVG IDs)
      // Need to map routes.json IDs back to SVG IDs
      const reverseRouteIdMapping: Record<string, string[]> = {
        'takamatsu_naoshima': ['route_takamatsu_miyanoura'],
        'uno_naoshima': ['route_uno_miyanoura'],
        'uno_naoshima_honmura': ['route_uno_honmura'],
        'naoshima_teshima_inujima': ['route_miyanoura_ieura_inujima'],
        'shodoshima_tonosho_naoshima_miyanoura': ['route_tonosho_miyanoura'],
        'takamatsu_teshima_karato': ['route_takamatsu_karato'],
        'takamatsu_shodoshima_ikeda': ['route_takamatsu_ikeda'],
        'shin_okayama_tonosho': ['route_shinokayama_tonosho'],
        'shodoshima_tonosho_inujima': ['route_tonosho_inujima'],
        'kyobashi_inujima_teshima': ['route_kyobashi_inujima_karato'],
        'takamatsu_honmura_ieura_via': ['route_takamatsu_honmura_ieura'],
        'uno_ieura_karato_tonosho': ['route_uno_ieura_karato_tonosho'],
        'takamatsu_megijima_ogijima': ['route_takamatsu_megijima_ogijima'],
        'takamatsu_oshima': ['route_takamatsu_oshima'],
        'hoden_inujima': ['route_hoden_inujima'],
        'himeji_fukuda': ['route_himeji_fukuda'],
        'takamatsu_tonosho': ['route_takamatsu_tonosho'],
        'kobe_sakate': ['route_kobe_sakate_takamatsuhigashi'],
        'kobe_takamatsu_higashi': ['route_kobe_sakate_takamatsuhigashi'],
        'takamatsu_higashi_sakate': ['route_kobe_sakate_takamatsuhigashi']
      };

      connectedRoutes.forEach((route: Route) => {
        const svgRouteIds = reverseRouteIdMapping[route.id] || [`route_${route.id}`];
        svgRouteIds.forEach(svgRouteId => {
          svg.select(`[id="${svgRouteId}"]`).classed('highlighted', true);
          svg.select(`path[id="${svgRouteId}"]`).classed('highlighted', true);
        });
      });
    } else if (selectedRoute) {
      // Highlight the selected route (add "route_" prefix for SVG IDs)
      const reverseRouteIdMapping: Record<string, string[]> = {
        'takamatsu_naoshima': ['route_takamatsu_miyanoura'],
        'uno_naoshima': ['route_uno_miyanoura'],
        'uno_naoshima_honmura': ['route_uno_honmura'],
        'naoshima_teshima_inujima': ['route_miyanoura_ieura_inujima'],
        'shodoshima_tonosho_naoshima_miyanoura': ['route_tonosho_miyanoura'],
        'takamatsu_teshima_karato': ['route_takamatsu_karato'],
        'takamatsu_shodoshima_ikeda': ['route_takamatsu_ikeda'],
        'shin_okayama_tonosho': ['route_shinokayama_tonosho'],
        'shodoshima_tonosho_inujima': ['route_tonosho_inujima'],
        'kyobashi_inujima_teshima': ['route_kyobashi_inujima_karato'],
        'takamatsu_honmura_ieura_via': ['route_takamatsu_honmura_ieura'],
        'uno_ieura_karato_tonosho': ['route_uno_ieura_karato_tonosho'],
        'takamatsu_megijima_ogijima': ['route_takamatsu_megijima_ogijima'],
        'takamatsu_oshima': ['route_takamatsu_oshima'],
        'hoden_inujima': ['route_hoden_inujima'],
        'himeji_fukuda': ['route_himeji_fukuda'],
        'takamatsu_tonosho': ['route_takamatsu_tonosho'],
        'kobe_sakate': ['route_kobe_sakate_takamatsuhigashi'],
        'kobe_takamatsu_higashi': ['route_kobe_sakate_takamatsuhigashi'],
        'takamatsu_higashi_sakate': ['route_kobe_sakate_takamatsuhigashi']
      };

      const svgRouteIds = reverseRouteIdMapping[selectedRoute.route.id] || [`route_${selectedRoute.route.id}`];
      svgRouteIds.forEach(svgRouteId => {
        svg.select(`[id="${svgRouteId}"]`).classed('highlighted', true);
        svg.select(`path[id="${svgRouteId}"]`).classed('highlighted', true);
      });
    }

    // Apply hovered class for 2nd level highlighting (takes precedence over highlighted)
    if (hoveredRouteId) {
      const reverseRouteIdMapping: Record<string, string[]> = {
        'takamatsu_naoshima': ['route_takamatsu_miyanoura'],
        'uno_naoshima': ['route_uno_miyanoura'],
        'uno_naoshima_honmura': ['route_uno_honmura'],
        'naoshima_teshima_inujima': ['route_miyanoura_ieura_inujima'],
        'shodoshima_tonosho_naoshima_miyanoura': ['route_tonosho_miyanoura'],
        'takamatsu_teshima_karato': ['route_takamatsu_karato'],
        'takamatsu_shodoshima_ikeda': ['route_takamatsu_ikeda'],
        'shin_okayama_tonosho': ['route_shinokayama_tonosho'],
        'shodoshima_tonosho_inujima': ['route_tonosho_inujima'],
        'kyobashi_inujima_teshima': ['route_kyobashi_inujima_karato'],
        'takamatsu_honmura_ieura_via': ['route_takamatsu_honmura_ieura'],
        'uno_ieura_karato_tonosho': ['route_uno_ieura_karato_tonosho'],
        'takamatsu_megijima_ogijima': ['route_takamatsu_megijima_ogijima'],
        'takamatsu_oshima': ['route_takamatsu_oshima'],
        'hoden_inujima': ['route_hoden_inujima'],
        'himeji_fukuda': ['route_himeji_fukuda'],
        'takamatsu_tonosho': ['route_takamatsu_tonosho'],
        'kobe_sakate': ['route_kobe_sakate_takamatsuhigashi'],
        'kobe_takamatsu_higashi': ['route_kobe_sakate_takamatsuhigashi'],
        'takamatsu_higashi_sakate': ['route_kobe_sakate_takamatsuhigashi']
      };

      const svgRouteIds = reverseRouteIdMapping[hoveredRouteId] || [`route_${hoveredRouteId}`];
      svgRouteIds.forEach(svgRouteId => {
        svg.select(`[id="${svgRouteId}"]`).classed('hovered', true);
        svg.select(`path[id="${svgRouteId}"]`).classed('hovered', true);
      });
    }
  }, [selectedPort, selectedRoute, mapPortIdToStopId, hoveredRouteId]);

  // Handle clicks on empty areas to deselect
  const handleBackgroundClick = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    // Check if click target is the SVG itself or the background group (not a port or route)
    const target = event.target as SVGElement;
    const tagName = target.tagName.toLowerCase();
    const targetId = target.id || '';

    // If clicking on the SVG, background, or non-interactive elements, deselect
    if (
      tagName === 'svg' ||
      tagName === 'g' ||
      (!targetId.includes('port') && !targetId.includes('route'))
    ) {
      setSelectedPort(null);
      setSelectedRoute(null);

      // If in departure board mode, also notify parent to clear selection
      if (onDeparturePortClick) {
        onDeparturePortClick('');
      }
    }
  }, [onDeparturePortClick]);

  // D3 Zoom Setup
  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;

    const svg = select(svgRef.current);
    const g = select(gRef.current);

    // Create zoom behavior
    const zoom = d3zoom<SVGSVGElement, unknown>()
      .scaleExtent([MIN_ZOOM, MAX_ZOOM])
      .translateExtent([
        [-VIEWBOX_WIDTH * 0.2, -VIEWBOX_HEIGHT * 0.2],
        [VIEWBOX_WIDTH * 1.2, VIEWBOX_HEIGHT * 1.2]
      ])
      .on('zoom', (event) => {
        // Apply transform directly to the group element via D3
        g.attr('transform', event.transform.toString());
        // Update zoom level and transform for display and panel positioning
        setZoomLevel(event.transform.k);
        setCurrentTransform({ k: event.transform.k, x: event.transform.x, y: event.transform.y });
      });

    // Apply zoom behavior to SVG (for event handling)
    svg.call(zoom);
    zoomBehavior.current = zoom;

    // Cleanup
    return () => {
      svg.on('.zoom', null);
    };
  }, []);

  return (
    <div
      className={`rounded-lg h-96 lg:h-full flex relative overflow-hidden ${className}`}
      style={{ touchAction: 'none', backgroundColor: '#E8E8E8' }}
    >
      {/* Interactive Map SVG */}
      <MapArtwork
        ref={svgRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onRouteClick={handleRouteClick}
        onPortClick={handlePortClick}
        onClick={handleBackgroundClick}
        style={{ touchAction: 'none' }}
        gRef={gRef}
      >
        <defs>
          <filter id="island-shadow">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.3"/>
          </filter>
          <style>
            {`
              [id$="_port"],
              [id$="_port"] path {
                fill: ${styleConfig.ports.default.fill};
                stroke: ${styleConfig.ports.default.stroke};
                stroke-width: ${styleConfig.ports.default.strokeWidth};
                transition: fill 0.25s ease, stroke-width 0.25s ease, opacity 0.25s ease;
                cursor: pointer;
              }
              [id$="_port"]:hover,
              [id$="_port"]:hover path {
                fill: ${styleConfig.ports.hover.fill};
                stroke-width: ${styleConfig.ports.hover.strokeWidth};
              }
              /* Selected port - blue */
              [id$="_port"].selected,
              [id$="_port"].selected path {
                fill: #3B82F6 !important;
                stroke-width: 2 !important;
              }
              /* Connected port - yellow (default) */
              [id$="_port"].connected,
              [id$="_port"].connected path {
                fill: ${styleConfig.ports.default.fill} !important;
                stroke-width: ${styleConfig.ports.default.strokeWidth} !important;
              }
              /* Inactive port - grey */
              [id$="_port"].inactive,
              [id$="_port"].inactive path {
                fill: #9CA3AF !important;
              }
              path[id^="route"],
              g[id^="route"]:not(#routes) path {
                stroke: ${styleConfig.routes.default.stroke};
                stroke-width: ${styleConfig.routes.default.strokeWidth};
                stroke-dasharray: ${styleConfig.routes.default.strokeDasharray};
                opacity: ${styleConfig.routes.default.opacity};
                transition: stroke-width 0.25s ease, stroke 0.25s ease, opacity 0.25s ease;
                cursor: pointer;
                stroke-linecap: round;
              }
              path[id^="route"]:hover,
              g[id^="route"]:not(#routes) path:hover {
                stroke: ${styleConfig.routes.hover.stroke};
                stroke-width: ${styleConfig.routes.hover.strokeWidth};
                opacity: ${styleConfig.routes.hover.opacity};
              }
              g[id^="route"]:not(#routes):hover path {
                stroke: ${styleConfig.routes.hover.stroke};
                stroke-width: ${styleConfig.routes.hover.strokeWidth};
                opacity: ${styleConfig.routes.hover.opacity};
              }
              
              
              /* Highlighted route styles (1st level - connected routes) */
              path[id^="route"].highlighted,
              g[id^="route"]:not(#routes).highlighted path {
                stroke: #3B82F6 !important;
                stroke-width: 4 !important;
                opacity: 1 !important;
                filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.6));
              }

              /* Hovered route styles (2nd level - lighter blue, overrides highlighted) */
              path[id^="route"].hovered,
              g[id^="route"]:not(#routes).hovered path {
                stroke: #93C5FD !important;
                stroke-width: 5 !important;
                opacity: 1 !important;
                filter: drop-shadow(0 0 6px rgba(147, 197, 253, 0.8));
              }
            `}
          </style>
        </defs>

        {/* Interactive Content Group */}
        <g>
          {/* Port Labels Only - Ports are now interactive in SVG */}
          <g id="Port_Labels">
            {mapPorts.map(port => {
              const centerX = port.x + port.width / 2;
              const centerY = port.y + port.height / 2;

              // Use custom offset if provided, otherwise use default positioning
              const labelOffset = port.labelOffset || { x: 0, y: styleConfig.portLabels.spacing.verticalOffset };
              const labelX = centerX + labelOffset.x;
              const labelY = centerY + labelOffset.y;

              return (
                <g key={`${port.id}_label`}>
                  {/* Primary Label (above) - Chinese for CN, Japanese for EN/JA */}
                  <text
                    x={labelX}
                    y={labelY + styleConfig.portLabels.spacing.primaryOffset}
                    className={language === 'zh' ? 'chinese-label' : 'japanese-label'}
                    {...(language === 'zh' ? getChineseLabelStyles() : getJapaneseLabelStyles())}
                  >
                    {language === 'zh' ? port.zh : port.ja}
                  </text>

                  {/* Secondary Label (below) - Always English */}
                  <text
                    x={labelX}
                    y={labelY + styleConfig.portLabels.spacing.secondaryOffset}
                    className="english-label"
                    {...getEnglishLabelStyles()}
                  >
                    {port.en}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Region/Island Labels - Non-clickable informational text */}
          <g id="Region_Labels" style={{ pointerEvents: 'none' }}>
            {/* Kagawa Prefecture */}
            <text
              x="100"
              y="480"
              fontSize={styleConfig.regionLabels.prefecture.fontSize}
              fontWeight={styleConfig.regionLabels.prefecture.fontWeight}
              fill={styleConfig.regionLabels.prefecture.fill}
              opacity={styleConfig.regionLabels.prefecture.opacity}
              fontFamily={language === 'zh' ? 'Noto Serif SC, sans-serif' : language === 'ja' ? 'Noto Serif JP, sans-serif' : 'Noto Serif, sans-serif'}
              textAnchor="middle"
              style={{ userSelect: 'none' }}
            >
              {language === 'zh' ? '香川县' : language === 'ja' ? '香川県' : 'Kagawa'}
            </text>

            {/* Okayama Prefecture */}
            <text
              x="70"
              y="100"
              fontSize={styleConfig.regionLabels.prefecture.fontSize}
              fontWeight={styleConfig.regionLabels.prefecture.fontWeight}
              fill={styleConfig.regionLabels.prefecture.fill}
              opacity={styleConfig.regionLabels.prefecture.opacity}
              fontFamily={language === 'zh' ? 'Noto Serif SC, sans-serif' : language === 'ja' ? 'Noto Serif JP, sans-serif' : 'Noto Serif, sans-serif'}
              textAnchor="middle"
              style={{ userSelect: 'none' }}
            >
              {language === 'zh' ? '冈山县' : language === 'ja' ? '岡山県' : 'Okayama'}
            </text>

            {/* Naoshima Island */}
            <text
              x="200"
              y="300"
              fontSize={styleConfig.regionLabels.island.fontSize}
              fontWeight={styleConfig.regionLabels.island.fontWeight}
              fill={styleConfig.regionLabels.island.fill}
              opacity={styleConfig.regionLabels.island.opacity}
              fontFamily={language === 'zh' ? 'Noto Serif SC, sans-serif' : language === 'ja' ? 'Noto Serif JP, sans-serif' : 'Noto Serif, sans-serif'}
              textAnchor="middle"
              style={{ userSelect: 'none' }}
            >
              {language === 'zh' ? '直岛' : language === 'ja' ? '直島' : 'Naoshima'}
            </text>

            {/* Teshima Island */}
            <text
              x="312"
              y="255"
              fontSize={styleConfig.regionLabels.island.fontSize}
              fontWeight={styleConfig.regionLabels.island.fontWeight}
              fill={styleConfig.regionLabels.island.fill}
              opacity={styleConfig.regionLabels.island.opacity}
              fontFamily={language === 'zh' ? 'Noto Serif SC, sans-serif' : language === 'ja' ? 'Noto Serif JP, sans-serif' : 'Noto Serif, sans-serif'}
              textAnchor="middle"
              style={{ userSelect: 'none' }}
            >
              {language === 'zh' ? '丰岛' : language === 'ja' ? '豊島' : 'Teshima'}
            </text>

            {/* Shodoshima Island */}
            <text
              x="630"
              y="180"
              fontSize={styleConfig.regionLabels.island.fontSize}
              fontWeight={styleConfig.regionLabels.island.fontWeight}
              fill={styleConfig.regionLabels.island.fill}
              opacity={styleConfig.regionLabels.island.opacity}
              fontFamily={language === 'zh' ? 'Noto Serif SC, sans-serif' : language === 'ja' ? 'Noto Serif JP, sans-serif' : 'Noto Serif, sans-serif'}
              textAnchor="middle"
              style={{ userSelect: 'none' }}
            >
              {language === 'zh' ? '小豆岛' : language === 'ja' ? '小豆島' : 'Shodoshima'}
            </text>
          </g>
        </g>
      </MapArtwork>

      {/* Route Info Panel */}
      {selectedRoute && (() => {
        const screenPos = svgToScreenCoords(selectedRoute.svgPosition.x, selectedRoute.svgPosition.y);
        return (
          <RouteInfoPanel
            route={selectedRoute.route}
            fromStop={selectedRoute.fromStop}
            toStop={selectedRoute.toStop}
            position={screenPos}
            onClose={() => setSelectedRoute(null)}
            onSwap={() => {
              // Swap from and to stops
              setSelectedRoute({
                ...selectedRoute,
                fromStop: selectedRoute.toStop,
                toStop: selectedRoute.fromStop
              });
            }}
            onApply={(fromId: string, toId: string) => {
              setFromStop?.(fromId);
              setToStop?.(toId);
              setSelectedRoute(null);
            }}
          />
        );
      })()}

      {/* Map Controls and Zoom Info */}
      <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end space-y-2">
        {/* Zoom Controls */}
        <div className="flex flex-col space-y-2">
          <button
            onClick={handleZoomIn}
            className="w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Zoom in"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>

          <button
            onClick={handleZoomOut}
            className="w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Zoom out"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
            </svg>
          </button>

          <button
            onClick={resetView}
            className="w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Reset view"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Zoom Info */}
        <div className="bg-white bg-opacity-90 rounded-lg px-3 py-2 shadow-md">
          <div className="text-xs text-gray-600">
            <span>Zoom: {(zoomLevel * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

    </div>
  );
};
